import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GUN_KISA, tarihStr, gunEkle } from '../components/randevu/randevuOrtak';
import '../components/takip/takip.css';
import '../components/randevu/randevu.css';

// Müşterinin personelin boş saatlerinden online randevu aldığı sayfa (oturum gerekmez).
// /randevu-al/{personelToken} ya da /randevu-al/genel (online randevuya açık tüm personel)
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5159';

interface Bilgi {
    firmaAdi: string;
    personel: { kullaniciID: number; adSoyad: string }[];
    turler: { turID: number; ad: string; varsayilanSure: number; renk: string; ikon?: string | null }[];
    ilkGun: string;
    sonGun: string;
}

const OnlineRandevu: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const [bilgi, setBilgi] = useState<Bilgi | null>(null);
    const [hata, setHata] = useState('');
    const [personelID, setPersonelID] = useState<number | null>(null);
    const [turID, setTurID] = useState<number | null>(null);
    const [gun, setGun] = useState('');
    const [saatler, setSaatler] = useState<string[] | null>(null);
    const [saat, setSaat] = useState('');
    const [ad, setAd] = useState('');
    const [telefon, setTelefon] = useState('');
    const [eposta, setEposta] = useState('');
    const [not, setNot] = useState('');
    const [onay, setOnay] = useState(false);
    const [gonderiliyor, setGonderiliyor] = useState(false);
    const [formHata, setFormHata] = useState('');

    useEffect(() => {
        fetch(`${API_BASE}/api/RandevuPortal/al/${token}`).then(async r => {
            const d = await r.json();
            if (!r.ok) { setHata(d.message || 'Bağlantı geçersiz.'); return; }
            setBilgi(d);
            if (d.personel.length === 1) setPersonelID(d.personel[0].kullaniciID);
            if (d.turler.length === 1) setTurID(d.turler[0].turID);
            setGun(d.ilkGun);
        }).catch(() => setHata('Bağlantı hatası. Lütfen daha sonra tekrar deneyin.'));
    }, [token]);

    const gunler = useMemo(() => {
        if (!bilgi) return [];
        const liste: Date[] = [];
        for (let d = new Date(bilgi.ilkGun + 'T00:00:00'); tarihStr(d) <= bilgi.sonGun && liste.length < 60; d = gunEkle(d, 1)) liste.push(d);
        return liste;
    }, [bilgi]);

    useEffect(() => {
        if (!personelID || !gun || !turID) { setSaatler(null); return; }
        setSaatler(null); setSaat('');
        fetch(`${API_BASE}/api/RandevuPortal/al/${token}/saatler?personelID=${personelID}&gun=${gun}&turID=${turID}`)
            .then(r => r.json()).then(d => setSaatler(Array.isArray(d) ? d : [])).catch(() => setSaatler([]));
    }, [token, personelID, gun, turID]);

    const gonder = async () => {
        setFormHata('');
        if (!ad.trim() || !telefon.trim()) { setFormHata('Ad soyad ve telefon gerekli.'); return; }
        if (!onay) { setFormHata('Bilgilerinizin randevu için kullanılmasını onaylayın.'); return; }
        setGonderiliyor(true);
        try {
            const r = await fetch(`${API_BASE}/api/RandevuPortal/al/${token}`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ personelID, turID, baslangic: `${gun}T${saat}:00`, ad, telefon, eposta: eposta || null, not: not || null }),
            });
            const d = await r.json();
            if (!r.ok) {
                setFormHata(d.message || 'Randevu alınamadı.');
                if (r.status === 409) { setSaat(''); setSaatler(null); setGun(g => g); fetch(`${API_BASE}/api/RandevuPortal/al/${token}/saatler?personelID=${personelID}&gun=${gun}&turID=${turID}`).then(x => x.json()).then(setSaatler).catch(() => { }); }
                return;
            }
            navigate(`/randevu/${d.musteriToken}`);
        } catch { setFormHata('Bağlantı hatası.'); }
        finally { setGonderiliyor(false); }
    };

    const tur = bilgi?.turler.find(t => t.turID === turID);

    return (
        <div className="rnd-acik">
            <div className="rnd-acik-kart">
                <div className="rnd-acik-bas">
                    <div className="firma">{bilgi?.firmaAdi}</div>
                    <h1>Online Randevu</h1>
                    {bilgi?.personel.length === 1 && <div style={{ color: '#64748b', fontSize: 14, marginTop: 2 }}>{bilgi.personel[0].adSoyad}</div>}
                </div>
                <div className="rnd-acik-govde">
                    {hata && <div className="tkp-hata">{hata}</div>}
                    {!bilgi && !hata && <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}><i className="fas fa-spinner fa-spin" style={{ fontSize: 24 }}></i></div>}
                    {bilgi && (
                        <>
                            {bilgi.personel.length > 1 && (
                                <>
                                    <div className="rnd-adim">1 · Görüşmek istediğiniz kişi</div>
                                    <div className="rnd-secim">
                                        {bilgi.personel.map(p => <button key={p.kullaniciID} className={personelID === p.kullaniciID ? 'aktif' : ''} onClick={() => setPersonelID(p.kullaniciID)}><i className="fas fa-user"></i>{p.adSoyad}</button>)}
                                    </div>
                                </>
                            )}
                            <div className="rnd-adim">{bilgi.personel.length > 1 ? '2' : '1'} · Randevu konusu</div>
                            <div className="rnd-secim">
                                {bilgi.turler.map(t => (
                                    <button key={t.turID} className={turID === t.turID ? 'aktif' : ''} onClick={() => setTurID(t.turID)}>
                                        <i className={`fas ${t.ikon || 'fa-calendar'}`} style={{ color: t.renk }}></i>{t.ad}<span style={{ fontSize: 11, color: '#94a3b8' }}>{t.varsayilanSure} dk</span>
                                    </button>
                                ))}
                            </div>

                            {personelID && turID && (
                                <>
                                    <div className="rnd-adim">Gün</div>
                                    <div className="rnd-gun-serit">
                                        {gunler.map(d => (
                                            <button key={tarihStr(d)} className={gun === tarihStr(d) ? 'aktif' : ''} onClick={() => setGun(tarihStr(d))}>
                                                <div className="g">{GUN_KISA[d.getDay()]}</div>
                                                <div className="n">{d.getDate()}</div>
                                                <div className="a">{d.toLocaleDateString('tr-TR', { month: 'short' })}</div>
                                            </button>
                                        ))}
                                    </div>
                                    <div className="rnd-adim">Saat</div>
                                    {saatler === null && <div className="tkp-bos"><i className="fas fa-spinner fa-spin"></i></div>}
                                    {saatler && saatler.length === 0 && <div className="tkp-bos">Bu gün için boş saat yok, başka bir gün seçin.</div>}
                                    {saatler && saatler.length > 0 && (
                                        <div className="rnd-secim">
                                            {saatler.map(s => <button key={s} className={saat === s ? 'aktif' : ''} onClick={() => setSaat(s)}>{s}</button>)}
                                        </div>
                                    )}
                                </>
                            )}

                            {saat && (
                                <>
                                    <div className="rnd-adim">Bilgileriniz</div>
                                    <div style={{ background: '#fff7ed', borderRadius: 10, padding: '8px 12px', fontSize: 13.5, color: '#9a3412', marginBottom: 10, fontWeight: 600 }}>
                                        <i className="fas fa-calendar-check" style={{ marginRight: 6 }}></i>
                                        {new Date(`${gun}T00:00:00`).toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })} {saat} · {tur?.ad}
                                    </div>
                                    {formHata && <div className="tkp-hata">{formHata}</div>}
                                    <div className="tkp-satir">
                                        <div className="tkp-alan"><label className="tkp-etiket">Ad soyad *</label><input className="tkp-girdi" value={ad} maxLength={150} onChange={e => setAd(e.target.value)} /></div>
                                        <div className="tkp-alan"><label className="tkp-etiket">Telefon *</label><input className="tkp-girdi" type="tel" value={telefon} maxLength={20} onChange={e => setTelefon(e.target.value)} placeholder="05xx xxx xx xx" /></div>
                                    </div>
                                    <div className="tkp-alan"><label className="tkp-etiket">E-posta</label><input className="tkp-girdi" type="email" value={eposta} maxLength={200} onChange={e => setEposta(e.target.value)} /></div>
                                    <div className="tkp-alan"><label className="tkp-etiket">Not</label><textarea className="tkp-girdi" rows={2} value={not} maxLength={1000} onChange={e => setNot(e.target.value)} placeholder="İlgilendiğiniz proje / daire tipi vb." /></div>
                                    <label style={{ display: 'flex', gap: 8, fontSize: 12.5, color: '#475569', alignItems: 'flex-start', marginBottom: 12, cursor: 'pointer' }}>
                                        <input type="checkbox" checked={onay} onChange={e => setOnay(e.target.checked)} style={{ marginTop: 2 }} />
                                        Bilgilerimin randevu oluşturmak ve randevuyla ilgili SMS / e-posta bildirimleri için {bilgi.firmaAdi} tarafından işlenmesini onaylıyorum.
                                    </label>
                                    <button className="tkp-btn turuncu" style={{ width: '100%', justifyContent: 'center', padding: 12, fontSize: 15 }} disabled={gonderiliyor} onClick={gonder}>
                                        <i className={`fas ${gonderiliyor ? 'fa-spinner fa-spin' : 'fa-check'}`}></i>Randevu al
                                    </button>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OnlineRandevu;
